import { useState, useEffect, useCallback } from 'react';
import { Building2, Swords } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import type { VendorIntelligence, VendorCompetitor, VerticalDifferentiator, VendorBattlecard } from '../../types';
import WhoWeAreTab from './WhoWeAreTab';
import CompetitorsTab from './CompetitorsTab';

const SUB_TABS = [
  { id: 'who' as const, label: 'Who We Are', icon: Building2 },
  { id: 'competitors' as const, label: 'Competitors', icon: Swords },
];

export default function MyDomain() {
  const { organisation } = useAuth();
  const { apiGet } = useApi();
  const [subTab, setSubTab] = useState<'who' | 'competitors'>('who');
  const [intelligence, setIntelligence] = useState<VendorIntelligence | null>(null);
  const [competitors, setCompetitors] = useState<VendorCompetitor[]>([]);
  const [differentiators, setDifferentiators] = useState<VerticalDifferentiator[]>([]);
  const [battlecards, setBattlecards] = useState<VendorBattlecard[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!organisation) return;
    const orgQs = `org_id=${encodeURIComponent(organisation.id)}`;
    const [intelRes, compRes, diffRes, bcRes] = await Promise.all([
      apiGet<{ intelligence: VendorIntelligence | null }>(`/v1/vendor-intelligence?${orgQs}`),
      apiGet<{ competitors: VendorCompetitor[] }>(`/v1/vendor-competitors?${orgQs}`),
      apiGet<{ differentiators: VerticalDifferentiator[] }>(`/v1/vendor-vertical-differentiators?${orgQs}`),
      apiGet<{ battlecards: VendorBattlecard[] }>(`/v1/vendor-battlecards?${orgQs}`),
    ]);
    // Preserve legacy gates: only show published intelligence and completed competitors.
    setIntelligence(intelRes.intelligence?.is_published ? intelRes.intelligence : null);
    setCompetitors((compRes.competitors ?? []).filter(c => c.status === 'completed'));
    setDifferentiators(diffRes.differentiators ?? []);
    setBattlecards(bcRes.battlecards ?? []);
    setLoading(false);
  }, [organisation, apiGet]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-app-line-strong border-t-forest-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!intelligence) {
    return (
      <div className="text-center py-20">
        <Building2 className="w-12 h-12 text-app-line-strong mx-auto mb-3" />
        <h3 className="text-base font-medium text-app-muted mb-1">Organisation Intelligence Not Available</h3>
        <p className="text-sm text-app-muted">Your admin has not yet completed the organisation intelligence setup.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-1 mb-6">
        {SUB_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              subTab === tab.id
                ? 'bg-forest-50 text-forest-700'
                : 'text-app-muted hover:text-app-ink hover:bg-app-canvas'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'who' && <WhoWeAreTab intelligence={intelligence} />}
      {subTab === 'competitors' && (
        <CompetitorsTab
          competitors={competitors}
          differentiators={differentiators}
          battlecards={battlecards}
          vendorIntelligence={intelligence}
        />
      )}
    </div>
  );
}
